import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import RedeemIcon from '@mui/icons-material/Redeem';
import SendIcon from '@mui/icons-material/Send';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarCrewMgmtIcon } from '../assets/icons/sidebar_crew_management.svg';
import { useNavigate, useParams } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import RankStar from '../components/RankStar';
import SkillRadarChart from '../components/SkillRadarChart';
import { crewApi, offersApi, skillSettingsApi, getApiErrorMessage } from '../services/api';
import { getRankTierColor } from '../constants/rankColors';
import {
  SPECIAL_OFFER_TEMPLATES,
  NAME_PLACEHOLDER,
  fillRecipientName,
} from '../constants/specialOfferTemplates';
import {
  CrewGamificationBundle,
  AdviceObject,
  AdviceStatus,
  SpecialOffer,
  SpecialOfferType,
  SpecialOfferStatus,
  SpecialOfferCreate,
  SpecialOfferTemplate,
  StoreManualAdvice,
  CrossStoreSkillItem,
  UnacquiredSkillItem,
} from '../types';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';

const PRIMARY_BLUE = '#0071e3';
const TRUST_RED = '#E83828'; // 信頼スコア / アラート赤（付録C.2）
// エンゲージメントバー グラデーション（橙→赤、付録C.3）
const ENGAGEMENT_GRADIENT = 'linear-gradient(90deg, #F18F03 0%, #E84024 100%)';
// 「特別オファーを送る」アラートボタン共通グラデーション（橙→赤、付録C.3）
const OFFER_GRADIENT = 'linear-gradient(90deg, #F18F03 0%, #E84024 100%)';
const OFFER_GRADIENT_HOVER = 'linear-gradient(90deg, #E07F00 0%, #D2351C 100%)';
const TRACK_GRAY = '#C4C4C4'; // 未充填トラック（付録C.3）
// COPILOT（育成アドバイス）紫系（付録C.2）
const COPILOT_PURPLE = '#6A3DC2';
const COPILOT_CARD_BORDER = '#EEEDFF';
const COPILOT_PILL = '#534DFF';

// 0-100 を 0-100% 幅に
const clampPct = (v: number): number => Math.max(0, Math.min(100, v));

// 時給を int 円表記で（current_hourly_wage は int 円配信。null は「-」）
const formatWage = (wage: number | null): string => {
  if (wage === null || wage === undefined) return '-';
  return `¥${Math.round(wage).toLocaleString('ja-JP')}`;
};

// スコアの小数1桁表示
const formatScore = (v: number): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '-';
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

// 日時を「yyyy/MM/dd HH:mm」表記で（null は「-」）
const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// 特別オファー / 招待状のステータス表示（ラベル・Chipカラー）
const OFFER_STATUS_META: Record<SpecialOfferStatus, { label: string; color: 'success' | 'default' | 'info' | 'warning' }> = {
  draft: { label: '下書き', color: 'default' },
  sent: { label: '送信済み・未回答', color: 'default' },
  opened: { label: '開封済み・未回答', color: 'info' },
  accepted: { label: '承諾されました', color: 'success' },
  declined: { label: '見送られました', color: 'default' },
  expired: { label: '期限切れ', color: 'warning' },
};

// ───────────────────────────────────────────────────────────────────────
// 特別オファー / 招待状 送信モーダル（p.7 デザイン）
// offer_type: special（特別オファー）/ invitation（招待状）。メッセージ + 報酬を入力し
// POST /api/store/{store_id}/special-offers で作成 + LINE送信する。
// ───────────────────────────────────────────────────────────────────────
interface SpecialOfferDialogProps {
  open: boolean;
  crewName: string;
  storeId: string | null;
  token: string;
  isSubmitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (data: { offer_type: SpecialOfferType; title: string; message_content: string; reward_content: string }) => void;
}

// 固定プリセット（API 取得失敗時のフォールバック）を店舗別テンプレと同一の描画形へ正規化する。
interface OfferTemplateItem {
  key: string;
  offer_type: SpecialOfferType;
  label: string;
  title: string;
  message: string;
  reward: string;
}

const SpecialOfferDialog: React.FC<SpecialOfferDialogProps> = ({
  open,
  crewName,
  storeId,
  token,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}) => {
  const [offerType, setOfferType] = useState<SpecialOfferType>('special');
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [reward, setReward] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<{ [k: string]: string }>({});
  // 店舗別テンプレ（共通∪店舗別のマージ）。取得失敗時は固定プリセットにフォールバックする。
  const [templates, setTemplates] = useState<SpecialOfferTemplate[] | null>(null);

  // ダイアログを開くたびにフォームをリセット
  useEffect(() => {
    if (open) {
      setOfferType('special');
      setTitle('');
      setMessage('');
      setReward('');
      setValidationErrors({});
    }
  }, [open]);

  // ダイアログを開いたときに店舗別テンプレを取得（失敗しても固定プリセットで動く）。
  useEffect(() => {
    if (!open || !storeId) return;
    let cancelled = false;
    offersApi
      .listTemplates(storeId, token)
      .then((rows) => { if (!cancelled) setTemplates(rows); })
      .catch(() => { if (!cancelled) setTemplates(null); });
    return () => { cancelled = true; };
  }, [open, storeId, token]);

  // 現在の種別で選べるテンプレ。DB取得できていれば店舗別テンプレ、無ければ固定プリセット。
  const templateItems: OfferTemplateItem[] = useMemo(() => {
    if (templates && templates.length > 0) {
      return templates
        .filter((t) => t.offer_type === offerType)
        .map((t) => ({
          key: t.template_id,
          offer_type: t.offer_type,
          label: t.name,
          title: t.title_template,
          message: t.message_template,
          reward: t.reward_template ?? '',
        }));
    }
    return SPECIAL_OFFER_TEMPLATES
      .filter((t) => t.offer_type === offerType)
      .map((t) => ({
        key: t.id,
        offer_type: t.offer_type,
        label: t.label,
        title: t.title,
        message: t.message,
        reward: t.reward,
      }));
  }, [templates, offerType]);

  const handleSubmit = () => {
    const errors: { [k: string]: string } = {};
    if (!title.trim()) {
      errors.title = 'タイトルを入力してください';
    }
    if (!message.trim()) {
      errors.message = 'メッセージを入力してください';
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    onSubmit({
      offer_type: offerType,
      title: title.trim(),
      message_content: message.trim(),
      reward_content: reward.trim(),
    });
  };

  const isInvitation = offerType === 'invitation';

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          m: 0,
          width: '100%',
          borderRadius: '16px 16px 0 0',
          maxHeight: '85vh',
          overflowY: 'auto',
        },
      }}
      sx={{
        '& .MuiDialog-container': { alignItems: 'flex-end' },
        '& .MuiDialog-paper': { m: 0, width: '100%', maxWidth: '100%' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700, fontSize: '0.95rem', py: 1.5 }}>
        {isInvitation ? (
          <MarkEmailUnreadIcon sx={{ color: '#E84024' }} />
        ) : (
          <LocalOfferIcon sx={{ color: '#E84024' }} />
        )}
        {isInvitation ? '招待状を送る' : '特別オファーを送る'}
        <Box
          onClick={isSubmitting ? undefined : onClose}
          sx={{
            ml: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: isSubmitting ? 'default' : 'pointer',
            color: isSubmitting ? '#ccc' : 'text.primary',
            px: 1,
          }}
        >
          <CloseIcon style={{ width: 28, height: 28 }} />
          <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, mt: '2px' }}>閉じる</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pb: { xs: 5, sm: 2 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 種別トグル: 特別オファー / 招待状 */}
        <ToggleButtonGroup
          value={offerType}
          exclusive
          fullWidth
          onChange={(_e, v) => { if (v) setOfferType(v as SpecialOfferType); }}
          sx={{
            mb: 2,
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#e0e0e0',
            },
            '& .Mui-selected': {
              color: '#fff !important',
              background: OFFER_GRADIENT,
              '&:hover': { background: OFFER_GRADIENT_HOVER },
            },
          }}
        >
          <ToggleButton value="special">
            <LocalOfferIcon sx={{ fontSize: 18, mr: 0.75 }} />
            特別オファー
          </ToggleButton>
          <ToggleButton value="invitation">
            <MarkEmailUnreadIcon sx={{ fontSize: 18, mr: 0.75 }} />
            招待状
          </ToggleButton>
        </ToggleButtonGroup>

        {/* 特別オファーテンプレ（店舗別化）: 共通∪店舗別のテンプレから選択。{{name}} は送信時に宛先名へ自動挿入 */}
        {templateItems.length > 0 && (
          <Box sx={{ mt: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#777' }}>テンプレートから選択</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
              {templateItems.map((t) => (
                <Chip
                  key={t.key}
                  label={t.label}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (isSubmitting) return;
                    setTitle(t.title);
                    setMessage(t.message);
                    setReward(t.reward);
                    setValidationErrors({});
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        <TextField
          label="タイトル"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (validationErrors.title) setValidationErrors((p) => { const n = { ...p }; delete n.title; return n; });
          }}
          fullWidth
          required
          margin="normal"
          placeholder={isInvitation ? '例: あなたへのキャリア面談のご案内' : '例: 特別時給アップのご案内'}
          error={!!validationErrors.title}
          helperText={validationErrors.title || ''}
          disabled={isSubmitting}
        />

        <TextField
          label="メッセージ"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (validationErrors.message) setValidationErrors((p) => { const n = { ...p }; delete n.message; return n; });
          }}
          fullWidth
          required
          multiline
          rows={5}
          margin="normal"
          placeholder="クルーへ伝えたいメッセージを入力してください。"
          error={!!validationErrors.message}
          helperText={validationErrors.message || ''}
          disabled={isSubmitting}
        />

        {/* 課題126: {{name}} を挿入（送信時に宛先クルー名へ自動置換） */}
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label="お名前を挿入"
            size="small"
            variant="outlined"
            onClick={() => {
              if (isSubmitting) return;
              setMessage((prev) => `${prev}${NAME_PLACEHOLDER}`);
              if (validationErrors.message) setValidationErrors((p) => { const n = { ...p }; delete n.message; return n; });
            }}
            sx={{ cursor: 'pointer' }}
          />
          <Typography variant="caption" sx={{ color: '#999', ml: 1 }}>
            {`「${NAME_PLACEHOLDER}」は送信時に ${crewName || 'クルー名'} へ置換されます`}
          </Typography>
        </Box>

        <TextField
          label="報酬・特典（任意）"
          value={reward}
          onChange={(e) => setReward(e.target.value)}
          fullWidth
          margin="normal"
          placeholder={isInvitation ? '例: GIFT TICKET 3,000円分' : '例: 時給+200円、ボーナス5,000円 など'}
          helperText="報酬や特典があれば入力してください"
          disabled={isSubmitting}
          InputProps={{
            startAdornment: <RedeemIcon sx={{ mr: 1, color: '#E84024' }} />,
          }}
        />

        {/* 課題126: 送信プレビュー（{{name}} を宛先クルー名へ置換して表示） */}
        {(title.trim() || message.trim()) && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f7f7f7', borderRadius: '10px' }}>
            <Typography variant="caption" sx={{ color: '#777', fontWeight: 700 }}>プレビュー</Typography>
            {title.trim() && (
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mt: 0.5 }}>
                {fillRecipientName(title, crewName)}
              </Typography>
            )}
            {message.trim() && (
              <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {fillRecipientName(message, crewName)}
              </Typography>
            )}
            {reward.trim() && (
              <Typography sx={{ fontSize: '0.8rem', color: '#E84024', mt: 0.5 }}>
                特典: {fillRecipientName(reward, crewName)}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          disabled={isSubmitting}
          size="small"
          sx={{ bgcolor: '#c4c4c4', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: '#b0b0b0', boxShadow: 'none' } }}
        >
          閉じる
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon />}
          sx={{
            background: OFFER_GRADIENT,
            boxShadow: 'none',
            fontWeight: 700,
            fontSize: '0.875rem',
            textTransform: 'none',
            '&:hover': { background: OFFER_GRADIENT_HOVER, boxShadow: 'none' },
          }}
        >
          {isSubmitting ? '送信中...' : '特別オファーを送る'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CrewDetail: React.FC = () => {
  const { storeUserId } = useParams<{ storeUserId: string }>();
  const navigate = useNavigate();
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [bundle, setBundle] = useState<CrewGamificationBundle | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // ランク未導出（勤務実績が1件も承認されていないクルー）。本人連携直後の事前登録クルーが
  // ここに該当する。障害ではないため赤いエラーではなく説明的な空状態を出す。
  const [rankPending, setRankPending] = useState<boolean>(false);
  // 横断スキル認識（050）: skill_definition_id → 他店舗での同義獲得情報
  const [crossByDef, setCrossByDef] = useState<Record<string, CrossStoreSkillItem>>({});
  // 同ランク未習得スキル
  const [unacquiredSkills, setUnacquiredSkills] = useState<UnacquiredSkillItem[]>([]);
  // 状態更新中のアドバイスID（ボタン二重押下防止）
  const [updatingAdviceId, setUpdatingAdviceId] = useState<string | null>(null);

  // 特別オファー / 招待状モーダル関連
  const [offerDialogOpen, setOfferDialogOpen] = useState<boolean>(false);
  const [offerSubmitting, setOfferSubmitting] = useState<boolean>(false);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [offerToastOpen, setOfferToastOpen] = useState<boolean>(false);
  const [offerToastMessage, setOfferToastMessage] = useState<string>('');
  const [offerToastSeverity, setOfferToastSeverity] = useState<'success' | 'error' | 'warning'>('success');
  // 送信済み特別オファー / 招待状の一覧（承諾/見送り結果の表示用）
  const [sentOffers, setSentOffers] = useState<SpecialOffer[]>([]);

  // 店舗手入力アドバイス（複数行、1クルー複数件）の編集ステート
  const [editingAdviceId, setEditingAdviceId] = useState<string | null>(null); // 編集中の既存アドバイスID
  const [editingDraft, setEditingDraft] = useState<string>('');
  const [isAddingAdvice, setIsAddingAdvice] = useState<boolean>(false);        // 新規追加フォーム表示中か
  const [newAdviceDraft, setNewAdviceDraft] = useState<string>('');
  const [isSavingManualAdvice, setIsSavingManualAdvice] = useState<boolean>(false);
  const [manualAdviceError, setManualAdviceError] = useState<string | null>(null);

  const fetchBundle = useCallback(async () => {
    if (!storeUserId) {
      setError('クルーIDが指定されていません');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setRankPending(false);
    try {
      const data = await crewApi.getCrewGamification(storeUserId, token);
      setBundle(data);
      setError(null);

      // 同ランク未習得スキル取得（ベストエフォート。失敗しても表示しないだけ）
      try {
        const tierCode = data.rank?.tier?.code;
        const unacquiredRes = await crewApi.getUnacquiredSkillsForCrew(
          storeUserId,
          token,
          tierCode ? { tier_code: tierCode } : {}
        );
        setUnacquiredSkills(unacquiredRes.unacquired);
      } catch {
        setUnacquiredSkills([]);
      }

      // 横断スキル認識（050）: 他店舗で同義スキルを保有しているかをベストエフォートで取得。
      // 機能フラグ OFF / 失敗時は単に表示しない。
      if (storeId) {
        try {
          const cross = await skillSettingsApi.getCrossStoreSkills(storeId, storeUserId, token);
          if (cross.enabled) {
            const byDef: Record<string, CrossStoreSkillItem> = {};
            for (const item of cross.items) {
              byDef[item.store_skill_definition_id] = item;
            }
            setCrossByDef(byDef);
          } else {
            setCrossByDef({});
          }
        } catch (e) {
          setCrossByDef({});
        }
      }
    } catch (err) {
      setBundle(null);
      // ランク未導出の 404 は障害ではなく「初回の勤怠承認待ち」。エラー表示ではなく空状態にする。
      // 同じ 404 は store_user が存在しない場合にも返るため detail 文字列も併せて判定し、
      // 一致しなければ従来のエラー表示にフォールバックする（BE の文言変更時も安全側に劣化）。
      const status = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (status === 404 && typeof detail === 'string' && detail.includes('ランクが未導出')) {
        setRankPending(true);
        setError(null);
      } else {
        setError(getApiErrorMessage(err, 'クルー情報の取得に失敗しました'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [storeUserId, token, storeId]);

  useEffect(() => {
    fetchBundle();
  }, [fetchBundle]);

  // 送信済み特別オファー / 招待状の一覧取得（承諾/見送り結果の表示用。ベストエフォート）
  const fetchSentOffers = useCallback(async () => {
    if (!storeUserId || !storeId) return;
    try {
      const offers = await offersApi.getCrewSpecialOffers(storeId, storeUserId, token);
      setSentOffers(offers);
    } catch {
      setSentOffers([]);
    }
  }, [storeId, storeUserId, token]);

  useEffect(() => {
    fetchSentOffers();
  }, [fetchSentOffers]);

  // 育成アドバイスの状態更新（dismiss / done）。成功後はローカル更新（再取得不要）。
  const handleAdviceStatus = async (advice: AdviceObject, status: AdviceStatus) => {
    if (!storeId) return;
    setUpdatingAdviceId(advice.advice_id);
    try {
      await crewApi.patchCopilotAdvice(storeId, advice.advice_id, status, token);
      setBundle((prev) =>
        prev
          ? {
              ...prev,
              advice: prev.advice.map((a) =>
                a.advice_id === advice.advice_id ? { ...a, status } : a
              ),
            }
          : prev
      );
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, '育成アドバイスの更新に失敗しました'));
    } finally {
      setUpdatingAdviceId(null);
    }
  };

  // 店舗手入力アドバイス（クルー単位、複数件）。
  // 入力値のバリデーション（共通）。OK なら trim 済み本文を返し、NG なら null。
  const validateManualAdvice = (raw: string): string | null => {
    const content = raw.trim();
    if (content.length === 0) {
      setManualAdviceError('本文を入力してください');
      return null;
    }
    if (content.length > 2000) {
      setManualAdviceError('2000文字以内で入力してください');
      return null;
    }
    return content;
  };

  // --- 新規追加 ---
  const handleStartAddManualAdvice = () => {
    setNewAdviceDraft('');
    setManualAdviceError(null);
    setEditingAdviceId(null); // 既存編集中があれば閉じる
    setIsAddingAdvice(true);
  };

  const handleCancelAddManualAdvice = () => {
    setIsAddingAdvice(false);
    setNewAdviceDraft('');
    setManualAdviceError(null);
  };

  const handleCreateManualAdvice = async () => {
    if (!storeUserId) return;
    const content = validateManualAdvice(newAdviceDraft);
    if (content === null) return;
    setIsSavingManualAdvice(true);
    setManualAdviceError(null);
    try {
      const created = await crewApi.createManualAdvice(storeUserId, content, token);
      // 新しい順（created_at desc）に揃えるため先頭へ追加。
      setBundle((prev) =>
        prev ? { ...prev, manual_advices: [created, ...(prev.manual_advices ?? [])] } : prev
      );
      setIsAddingAdvice(false);
      setNewAdviceDraft('');
    } catch (err) {
      setManualAdviceError(getApiErrorMessage(err, 'アドバイスの登録に失敗しました'));
    } finally {
      setIsSavingManualAdvice(false);
    }
  };

  // --- 既存アドバイスの編集 ---
  const handleStartEditManualAdvice = (item: StoreManualAdvice) => {
    setEditingAdviceId(item.advice_id);
    setEditingDraft(item.content);
    setManualAdviceError(null);
    setIsAddingAdvice(false); // 新規追加中があれば閉じる
  };

  const handleCancelEditManualAdvice = () => {
    setEditingAdviceId(null);
    setEditingDraft('');
    setManualAdviceError(null);
  };

  const handleUpdateManualAdvice = async (adviceId: string) => {
    if (!storeUserId) return;
    const content = validateManualAdvice(editingDraft);
    if (content === null) return;
    setIsSavingManualAdvice(true);
    setManualAdviceError(null);
    try {
      const saved = await crewApi.updateManualAdvice(storeUserId, adviceId, content, token);
      setBundle((prev) =>
        prev
          ? {
              ...prev,
              manual_advices: (prev.manual_advices ?? []).map((a) =>
                a.advice_id === adviceId ? saved : a
              ),
            }
          : prev
      );
      setEditingAdviceId(null);
      setEditingDraft('');
    } catch (err) {
      setManualAdviceError(getApiErrorMessage(err, 'アドバイスの保存に失敗しました'));
    } finally {
      setIsSavingManualAdvice(false);
    }
  };

  // --- 削除 ---
  const handleDeleteManualAdvice = async (adviceId: string) => {
    if (!storeUserId) return;
    if (!window.confirm('このアドバイスを削除しますか？')) return;
    setIsSavingManualAdvice(true);
    setManualAdviceError(null);
    try {
      await crewApi.deleteManualAdvice(storeUserId, adviceId, token);
      setBundle((prev) =>
        prev
          ? {
              ...prev,
              manual_advices: (prev.manual_advices ?? []).filter(
                (a) => a.advice_id !== adviceId
              ),
            }
          : prev
      );
      if (editingAdviceId === adviceId) {
        setEditingAdviceId(null);
        setEditingDraft('');
      }
    } catch (err) {
      setManualAdviceError(getApiErrorMessage(err, 'アドバイスの削除に失敗しました'));
    } finally {
      setIsSavingManualAdvice(false);
    }
  };

  // 特別オファー / 招待状の送信（POST /api/store/{store_id}/special-offers）。
  // 成功でモーダルを閉じてトースト表示。
  const handleSubmitOffer = async (data: {
    offer_type: SpecialOfferType;
    title: string;
    message_content: string;
    reward_content: string;
  }) => {
    if (!storeId || !storeUserId) {
      setOfferError('店舗IDまたはクルーIDが取得できませんでした');
      return;
    }
    setOfferSubmitting(true);
    setOfferError(null);
    try {
      const payload: SpecialOfferCreate = {
        store_user_id: storeUserId,
        offer_type: data.offer_type,
        title: data.title,
        message_content: data.message_content,
        reward_content: data.reward_content || undefined,
      };
      const created = await offersApi.createSpecialOffer(storeId, payload, token);
      setOfferDialogOpen(false);
      const label = data.offer_type === 'invitation' ? '招待状' : '特別オファー';
      // オファーは保存されるが LINE push はベストエフォート（§10-4）。push が 'sent' 以外なら
      // 「保存はできたが通知は届かなかった」ことを店長に警告表示する（成功偽装しない）。
      if (created.line_push_status && created.line_push_status !== 'sent') {
        const reason = created.line_push_error || created.line_push_status;
        const shortReason = reason.length > 120 ? `${reason.slice(0, 120)}…` : reason;
        setOfferToastMessage(`${label}を保存しましたが、LINE通知は届きませんでした（${shortReason}）`);
        setOfferToastSeverity('warning');
      } else {
        setOfferToastMessage(`${label}を送信しました`);
        setOfferToastSeverity('success');
      }
      setOfferToastOpen(true);
      fetchSentOffers();
    } catch (err) {
      setOfferError(getApiErrorMessage(err, '送信に失敗しました'));
    } finally {
      setOfferSubmitting(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <PageHeader
          title="クルー管理／詳細"
          icon={<SvgIcon component={SidebarCrewMgmtIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // ランク未導出（初回の勤怠承認前）。管理画面で事前登録し本人がLINE連携したばかりのクルーが該当する。
  if (rankPending) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <PageHeader
          title="クルー管理／詳細"
          icon={<SvgIcon component={SidebarCrewMgmtIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
        />
        <Alert severity="info" sx={{ mb: 2 }}>
          このクルーはまだ勤務実績が承認されていないため、ランク・スキル情報は未導出です。
          初回の勤怠承認後に表示されます。
        </Alert>
        <Button variant="outlined" size="small" onClick={() => navigate('/crew-management')}>
          クルー管理へ戻る
        </Button>
      </Box>
    );
  }

  if (error || !bundle) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <PageHeader
          title="クルー管理／詳細"
          icon={<SvgIcon component={SidebarCrewMgmtIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
        />
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'クルー情報が見つかりませんでした'}
        </Alert>
      </Box>
    );
  }

  const { rank, engagement, parameters, skills, advice } = bundle;
  // 育成アドバイスは active のみ表示（dismissed/done/expired は非表示）
  const activeAdvice = advice.filter((a) => a.status === 'active');
  // 店舗手入力アドバイス（1クルー複数件、新しい順。未登録なら空配列）
  const manualAdvices = bundle.manual_advices ?? [];

  return (
    <Box sx={{ flexGrow: 1, pb: 3 }}>
      <PageHeader
        title="クルー管理／詳細"
        icon={<SvgIcon component={SidebarCrewMgmtIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      {/* プロフィールヘッダー（PDF p.6：アバター＋氏名＋ランク＋トークアイコン） */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: '12px',
          boxShadow: 'none',
          border: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={bundle.photo_url || undefined}
            sx={{ width: 64, height: 64, bgcolor: '#cfd4da', flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#1d2530', lineHeight: 1.2 }} noWrap>
              {bundle.display_name || 'クルー'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              <RankStar
                tierCode={rank.tier?.code}
                color={rank.tier?.color}
                label={rank.tier?.name}
                showLabel
                size={24}
              />
              <Chip label={`Lv.${parameters.level}`} size="small" sx={{ fontWeight: 700, bgcolor: '#f0f0f0' }} />
              {rank.is_manual_override && (
                <Chip label="手動設定" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
              )}
              {/* 勤務実績ゲートのキャップ注記は、バンドルAPIが capped_by_work_performance/skill_tier を
                  まだ配信しないため省略（将来バックエンドで RankObject に追加された際に再掲する）。 */}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* エンゲージメント（定着度）バー（PDF p.6：名前直下） */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', boxShadow: 'none', border: 'none', bgcolor: 'transparent' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#3c3c3c' }}>
            エンゲージメント（定着度）
          </Typography>
          <Typography sx={{ fontWeight: 700, color: '#3c3c3c' }}>
            {formatScore(engagement.retention_rate)}
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              / 100
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ position: 'relative', height: 12, borderRadius: 6, bgcolor: TRACK_GRAY, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${clampPct(engagement.retention_rate)}%`,
              background: ENGAGEMENT_GRADIENT,
              borderRadius: 6,
              transition: 'width 0.4s ease',
            }}
          />
        </Box>
      </Paper>

      {/* 統計ボックス: 総勤務数 / 信頼スコア / 現在の時給 */}
      <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
        <Grid item xs={4} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.5, borderRadius: '12px', boxShadow: 'none', border: '1px solid #e0dfdc', textAlign: 'center', bgcolor: '#fff', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              総勤務数
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#3c3c3c' }}>
              {rank.total_shifts}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              回
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.5, borderRadius: '12px', boxShadow: 'none', border: '1px solid #e0dfdc', textAlign: 'center', bgcolor: '#fff', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              信頼スコア
            </Typography>
            {engagement.is_provisional ? (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#9e9e9e' }}>
                  —
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  評価前
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: TRUST_RED }}>
                  {formatScore(engagement.trust_score)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / 100
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.5, borderRadius: '12px', boxShadow: 'none', border: '1px solid #e0dfdc', textAlign: 'center', bgcolor: '#fff', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              現在の時給
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: PRIMARY_BLUE }}>
              {formatWage(rank.current_hourly_wage)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 獲得スキル一覧 */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', boxShadow: 'none', border: 'none', bgcolor: 'transparent' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#3c3c3c', mb: 1.5 }}>
          獲得スキル一覧
        </Typography>
        {skills.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            獲得したスキルはまだありません
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {skills.map((skill) => {
              const color = skill.tier?.color || getRankTierColor(skill.tier?.code);
              const ownedElsewhere = !!crossByDef[skill.skill_id]?.already_owned_via_other_stores;
              return (
                <Box
                  key={skill.skill_id}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${color}33`,
                    bgcolor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: `${color}1A`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <EmojiEventsRoundedIcon sx={{ color, fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, lineHeight: 1.3, color: '#3c3c3c' }}>
                      {skill.name}
                    </Typography>
                    {ownedElsewhere && (
                      <Chip
                        size="small"
                        label="他店舗でも保有"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          fontSize: '0.6rem',
                          bgcolor: '#fff4e5',
                          color: '#b26a00',
                          border: '1px solid #ffcc80',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* 同ランクの未習得スキル（横スクロール） */}
        {unacquiredSkills.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: '#898989', fontWeight: 600, display: 'block', mb: 1 }}>
              同ランクの未習得スキル
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 0.5,
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-track': { bgcolor: '#f0f0f0', borderRadius: 2 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#c4c4c4', borderRadius: 2 },
              }}
            >
              {unacquiredSkills.map((skill) => {
                const color = skill.tier?.color || getRankTierColor(skill.tier?.code);
                return (
                  <Box
                    key={skill.skill_definition_id}
                    sx={{
                      flexShrink: 0,
                      minWidth: 140,
                      borderRadius: 2,
                      border: `1px solid ${color}33`,
                      bgcolor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      px: 1.5,
                      py: 1,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: `${color}1A`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <EmojiEventsRoundedIcon sx={{ color, fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, lineHeight: 1.3, color: '#3c3c3c' }}>
                      {skill.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Paper>

      {/* 育成アドバイス（RECRUITING COPILOT）カード */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: '12px',
          boxShadow: 'none',
          border: 'none',
          bgcolor: '#fbfaff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: COPILOT_PILL,
              color: '#fff',
              borderRadius: '12px',
              px: 1,
              py: 0.4,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 14, color: '#fff' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.02em' }}>
              RECRUITING COPILOT
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: COPILOT_PURPLE }}>
            育成アドバイスのご提案
          </Typography>
        </Box>

        {activeAdvice.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            現在、表示できる育成アドバイスはありません
          </Typography>
        ) : (
          activeAdvice.map((a) => (
            <Box
              key={a.advice_id}
              sx={{
                bgcolor: '#fff',
                border: '1px solid #ece9ff',
                borderRadius: '10px',
                p: 1.5,
                mb: 1.25,
                '&:last-of-type': { mb: 0 },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3c3c3c', mb: 0.5 }}>
                {a.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', whiteSpace: 'pre-wrap' }}>
                {a.body}
              </Typography>
              {a.recommendation && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: '8px',
                    bgcolor: COPILOT_CARD_BORDER,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: COPILOT_PURPLE }}>
                    おすすめのアクション
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#444' }}>
                    {a.recommendation}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
                <Button
                  size="small"
                  startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  onClick={() => handleAdviceStatus(a, 'dismissed')}
                  disabled={updatingAdviceId === a.advice_id}
                  sx={{ color: '#898989', textTransform: 'none' }}
                >
                  非表示
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                  onClick={() => handleAdviceStatus(a, 'done')}
                  disabled={updatingAdviceId === a.advice_id}
                  sx={{
                    bgcolor: COPILOT_PURPLE,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#582fab', boxShadow: 'none' },
                  }}
                >
                  対応済み
                </Button>
              </Box>
            </Box>
          ))
        )}
      </Paper>

      {/* 店舗手入力の育成アドバイス（1クルー複数件。COPILOT 提案とは別物） */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: '12px',
          boxShadow: 'none',
          border: '1px solid #e0dfdc',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: '#3c3c3c' }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#3c3c3c' }}>
              店舗からの育成アドバイス
            </Typography>
          </Box>
          {!isAddingAdvice && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleStartAddManualAdvice}
              sx={{ textTransform: 'none', color: PRIMARY_BLUE }}
            >
              追加
            </Button>
          )}
        </Box>

        {manualAdviceError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {manualAdviceError}
          </Alert>
        )}

        {/* 新規追加フォーム */}
        {isAddingAdvice && (
          <Box
            sx={{
              border: '1px dashed #c9c7c3',
              borderRadius: '10px',
              p: 1.5,
              mb: 1.5,
              bgcolor: '#fafafa',
            }}
          >
            <TextField
              value={newAdviceDraft}
              onChange={(e) => setNewAdviceDraft(e.target.value)}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
              placeholder="このクルーへの育成アドバイスを入力してください"
              inputProps={{ maxLength: 2000 }}
              disabled={isSavingManualAdvice}
              autoFocus
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {newAdviceDraft.length} / 2000
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleCancelAddManualAdvice}
                  disabled={isSavingManualAdvice}
                  sx={{ textTransform: 'none', color: '#898989' }}
                >
                  キャンセル
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleCreateManualAdvice}
                  disabled={isSavingManualAdvice}
                  sx={{
                    bgcolor: PRIMARY_BLUE,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#005bb5', boxShadow: 'none' },
                  }}
                >
                  登録
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* 既存アドバイス一覧（新しい順） */}
        {manualAdvices.length === 0 && !isAddingAdvice ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
            まだアドバイスは登録されていません
          </Typography>
        ) : (
          manualAdvices.map((item) => (
            <Box
              key={item.advice_id}
              sx={{
                border: '1px solid #ece9e6',
                borderRadius: '10px',
                p: 1.5,
                mb: 1.25,
                '&:last-of-type': { mb: 0 },
                bgcolor: '#fff',
              }}
            >
              {editingAdviceId === item.advice_id ? (
                <Box>
                  <TextField
                    value={editingDraft}
                    onChange={(e) => setEditingDraft(e.target.value)}
                    multiline
                    minRows={4}
                    maxRows={12}
                    fullWidth
                    placeholder="このクルーへの育成アドバイスを入力してください"
                    inputProps={{ maxLength: 2000 }}
                    disabled={isSavingManualAdvice}
                    autoFocus
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {editingDraft.length} / 2000
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={handleCancelEditManualAdvice}
                        disabled={isSavingManualAdvice}
                        sx={{ textTransform: 'none', color: '#898989' }}
                      >
                        キャンセル
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleUpdateManualAdvice(item.advice_id)}
                        disabled={isSavingManualAdvice}
                        sx={{
                          bgcolor: PRIMARY_BLUE,
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#005bb5', boxShadow: 'none' },
                        }}
                      >
                        保存
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#3c3c3c', whiteSpace: 'pre-wrap', flexGrow: 1, minWidth: 0, wordBreak: 'break-word' }}
                    >
                      {item.content}
                    </Typography>
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleStartEditManualAdvice(item)}
                        disabled={isSavingManualAdvice}
                        aria-label="編集"
                        sx={{ color: '#898989' }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteManualAdvice(item.advice_id)}
                        disabled={isSavingManualAdvice}
                        aria-label="削除"
                        sx={{ color: '#c0504a' }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  {item.updated_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      最終更新: {new Date(item.updated_at).toLocaleString('ja-JP')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ))
        )}
      </Paper>

      {/* スキルパラメーター レーダー（5軸・自前SVG。PDF p.6：COPILOT の下） */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', boxShadow: 'none', border: 'none', bgcolor: 'transparent' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#3c3c3c', mb: 1 }}>
          スキルパラメーター
        </Typography>
        <Box sx={{ border: '1px solid #e0dfdc', bgcolor: '#fff', borderRadius: '12px', p: 2 }}>
          {parameters.is_provisional ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 5,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <AssessmentOutlinedIcon sx={{ fontSize: 40, color: '#bdbdbd', mb: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#3c3c3c' }}>
                勤務実績が不足しています（評価前）
              </Typography>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                数回の勤務が承認されるとスキルパラメーターが表示されます
              </Typography>
            </Box>
          ) : (
            <SkillRadarChart
              values={{
                interpersonal: parameters.interpersonal,
                leadership: parameters.leadership,
                discipline: parameters.discipline,
                knowledge: parameters.knowledge,
                execution: parameters.execution,
              }}
              is_provisional={parameters.is_provisional}
            />
          )}
        </Box>
      </Paper>

      {/* 特別オファーを送る（PDF p.6：最下部。アラート共通グラデーション 付録C.3） */}
      <Button
        fullWidth
        variant="contained"
        startIcon={<LocalOfferIcon />}
        onClick={() => { setOfferError(null); setOfferDialogOpen(true); }}
        sx={{
          mb: 1,
          py: 1.4,
          borderRadius: '12px',
          fontWeight: 700,
          fontSize: '1rem',
          textTransform: 'none',
          color: '#fff',
          background: OFFER_GRADIENT,
          boxShadow: 'none',
          '&:hover': { background: OFFER_GRADIENT_HOVER, boxShadow: 'none' },
        }}
      >
        特別オファーを送る
      </Button>

      {/* 送信履歴（承諾/見送り結果の表示） */}
      {sentOffers.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            送信履歴
          </Typography>
          {[...sentOffers]
            .sort((a, b) => new Date(b.sent_at || b.created_at || 0).getTime() - new Date(a.sent_at || a.created_at || 0).getTime())
            .map((offer) => {
              const statusMeta = OFFER_STATUS_META[offer.status];
              return (
                <Paper
                  key={offer.offer_id}
                  variant="outlined"
                  sx={{ p: 1.5, mb: 1, borderRadius: '10px' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={offer.offer_type === 'invitation' ? '招待状' : '特別オファー'}
                        variant="outlined"
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {offer.title}
                      </Typography>
                    </Box>
                    <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {/* created_at はDBサーバー既定値（本番はUTC）のため9時間ずれる。JSTで打刻される sent_at を使う */}
                    送信日時: {formatDateTime(offer.sent_at || offer.created_at)}
                    {offer.responded_at && ` / 回答日時: ${formatDateTime(offer.responded_at)}`}
                  </Typography>
                </Paper>
              );
            })}
        </Box>
      )}

      {/* 特別オファー / 招待状 送信モーダル */}
      <SpecialOfferDialog
        open={offerDialogOpen}
        crewName={bundle.display_name || ''}
        storeId={storeId}
        token={token}
        isSubmitting={offerSubmitting}
        error={offerError}
        onClose={() => setOfferDialogOpen(false)}
        onSubmit={handleSubmitOffer}
      />

      {/* 送信結果トースト */}
      <Snackbar
        open={offerToastOpen}
        autoHideDuration={5000}
        onClose={() => setOfferToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setOfferToastOpen(false)}
          severity={offerToastSeverity}
        >
          {offerToastMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default CrewDetail;
